import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
import { FormService } from '../services';
import {
  CancelFormTemplateMapPage,
  ClearFormTemplateMapError,
  CreateFormTemplateMap,
  CreateFormTemplateMapOnServer,
  LoadFormTemplateMapPage,
  FormTemplateMapActionTypes,
  RequestFormTemplateMapPage,
  ThrowFormTemplateMapError,
  ToggleFormTemplateMapActionLoading,
  ToggleFormTemplateMapPageLoading,
  UpdateFormTemplateMap,
  UpdateFormTemplateMapOnServer,
} from '../actions';

@Injectable()
export class FormTemplateMapEffects {
  showPageLoading = new ToggleFormTemplateMapPageLoading({ pageLoading: true });
  hidePageLoading = new ToggleFormTemplateMapPageLoading({ pageLoading: false });
  showActionLoading = new ToggleFormTemplateMapActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleFormTemplateMapActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private formService: FormService,
  ) {}

  @Effect()
  loadFormTemplateMapPage$ = this.actions$.pipe(
    ofType<RequestFormTemplateMapPage>(FormTemplateMapActionTypes.RequestFormTemplateMapPage),
    map((action: RequestFormTemplateMapPage) => action.payload),
    mergeMap(({ query }) => {
      this.store.dispatch(this.showPageLoading);
      const queryResult$ = this.formService.getFormTemplateMapPage(query);
      const observables = [queryResult$, of(query)];

      return forkJoin(observables).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.length) {
            this.store.dispatch(new ClearFormTemplateMapError());
            this.store.dispatch(this.hidePageLoading);

            return new LoadFormTemplateMapPage({
              formTemplateMaps: res[0].data,
              totalCount: res[0].count,
              query: res[1],
            });
          }

          this.store.dispatch(new CancelFormTemplateMapPage());

          return new ThrowFormTemplateMapError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  createFormTemplateMap$ = this.actions$.pipe(
    ofType<CreateFormTemplateMapOnServer>(FormTemplateMapActionTypes.CreateFormTemplateMapOnServer),
    map((action: CreateFormTemplateMapOnServer) => action.payload),
    mergeMap(({ formTemplateMap }) => {
      this.store.dispatch(this.showActionLoading);

      return this.formService.createFormTemplateMap(formTemplateMap).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearFormTemplateMapError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateFormTemplateMap({ formTemplateMap: res.body?.data });
          }

          return new ThrowFormTemplateMapError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateFormTemplateMap$ = this.actions$.pipe(
    ofType<UpdateFormTemplateMapOnServer>(FormTemplateMapActionTypes.UpdateFormTemplateMapOnServer),
    map((action: UpdateFormTemplateMapOnServer) => action.payload),
    mergeMap(({ formTemplateMap }) => {
      this.store.dispatch(this.showActionLoading);

      return this.formService.updateFormTemplateMap(formTemplateMap).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearFormTemplateMapError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateFormTemplateMap({ formTemplateMap: res.body?.data });
          }

          return new ThrowFormTemplateMapError({ error: res });
        }),
      );
    }),
  );
}
